import React from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  useTheme,
  useMediaQuery,
  Chip,
  Stack,
} from '@mui/material';
import GroupsIcon from '@mui/icons-material/Groups';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import PsychologyIcon from '@mui/icons-material/Psychology';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import PsychologyAltIcon from '@mui/icons-material/PsychologyAlt';
import BlockchainIcon from '@mui/icons-material/AccountBalance';
import MobileIcon from '@mui/icons-material/PhoneAndroid';
import TeacherIcon from '@mui/icons-material/Person';
import VoiceIcon from '@mui/icons-material/RecordVoiceOver';
import StorageIcon from '@mui/icons-material/Storage';

const Features: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const featureCategories = [
    {
      title: 'AI-Powered Learning',
      color: '#3b82f6',
      features: [
        {
          icon: PsychologyIcon,
          title: 'Advanced RAG System',
          description: 'Retrieval-Augmented Generation with multi-modal search, context-aware responses, and knowledge graph construction.',
          color: '#3b82f6',
        },
        {
          icon: AutoAwesomeIcon,
          title: 'Personalized AI Tutor',
          description: 'Adaptive learning that adjusts difficulty, provides custom feedback, and recognizes learning styles.',
          color: '#8b5cf6',
        },
        {
          icon: VoiceIcon,
          title: 'Multi-Modal AI',
          description: 'Voice recognition, image analysis, video processing, and gesture recognition for hands-free interaction.',
          color: '#60a5fa',
        },
        {
          icon: PsychologyAltIcon,
          title: 'Predictive Analytics',
          description: 'AI-powered learning path prediction, performance forecasting, and proactive intervention recommendations.',
          color: '#a78bfa',
        },
      ],
    },
    {
      title: 'Comprehensive Analytics',
      color: '#3b82f6',
      features: [
        {
          icon: AnalyticsIcon,
          title: 'Real-time Progress Tracking',
          description: 'Live session tracking, progress indicators, achievement monitoring, and streak counting.',
          color: '#3b82f6',
        },
        {
          icon: TrendingUpIcon,
          title: 'Performance Analytics',
          description: 'Subject performance analysis, time-based trends, comparative analysis, and skill assessment.',
          color: '#60a5fa',
        },
        {
          icon: PsychologyIcon,
          title: 'Learning Pattern Analysis',
          description: 'Study habit analysis, peak performance identification, motivation factors, and environment impact.',
          color: '#8b5cf6',
        },
        {
          icon: AutoAwesomeIcon,
          title: 'Personalized Insights',
          description: 'Strength identification, improvement areas, study recommendations, and learning path optimization.',
          color: '#a78bfa',
        },
      ],
    },
    {
      title: 'Multiplayer & Social',
      color: '#8b5cf6',
      features: [
        {
          icon: GroupsIcon,
          title: 'Real-time Multiplayer Games',
          description: 'Live quiz games with synchronized game state, instant feedback, and dynamic scoring.',
          color: '#8b5cf6',
        },
        {
          icon: EmojiEventsIcon,
          title: 'Social Learning Features',
          description: 'Friend integration, team formation, collaborative activities, and knowledge sharing.',
          color: '#a78bfa',
        },
        {
          icon: TrendingUpIcon,
          title: 'Competitive Learning',
          description: 'Leaderboards, achievement systems, peer interaction analysis, and social learning effectiveness.',
          color: '#c4b5fd',
        },
        {
          icon: GroupsIcon,
          title: 'Community Features',
          description: 'Player profiles, chat systems, group learning sessions, and collaborative study tools.',
          color: '#ddd6fe',
        },
      ],
    },
    {
      title: 'Blockchain & Rewards',
      color: '#3b82f6',
      features: [
        {
          icon: BlockchainIcon,
          title: 'Learning Coins (LC)',
          description: 'Earn coins through study activities, achievements, and long-term engagement rewards.',
          color: '#3b82f6',
        },
        {
          icon: EmojiEventsIcon,
          title: 'NFT Achievements',
          description: 'Unique NFT achievement badges, staking mechanisms, and governance token participation.',
          color: '#8b5cf6',
        },
        {
          icon: StorageIcon,
          title: 'Decentralized Storage',
          description: 'IPFS integration, content verification, immutable records, and data sovereignty.',
          color: '#60a5fa',
        },
        {
          icon: TrendingUpIcon,
          title: 'Gamification System',
          description: 'Study achievements, social achievements, skill mastery, innovation rewards, and leadership badges.',
          color: '#a78bfa',
        },
      ],
    },
    {
      title: 'Mobile & Accessibility',
      color: '#8b5cf6',
      features: [
        {
          icon: MobileIcon,
          title: 'Cross-Platform Mobile App',
          description: 'React Native app with offline capabilities, push notifications, and touch-optimized interface.',
          color: '#8b5cf6',
        },
        {
          icon: VoiceIcon,
          title: 'Voice & Gesture Controls',
          description: 'Voice commands, haptic feedback, biometric authentication, and gesture recognition.',
          color: '#a78bfa',
        },
        {
          icon: TeacherIcon,
          title: 'Teacher Portal',
          description: 'Class management, content creation tools, student analytics, and assessment generation.',
          color: '#c4b5fd',
        },
        {
          icon: AutoAwesomeIcon,
          title: 'Accessibility Features',
          description: 'VoiceOver support, TalkBack compatibility, high contrast modes, and screen reader optimization.',
          color: '#ddd6fe',
        },
      ],
    },
  ];

  return (
    <Box 
      component="section" 
      id="features" 
      sx={{ 
        py: { xs: 4, md: 8 }, 
        bgcolor: theme.palette.mode === 'dark' ? '#0f172a' : '#ffffff',
        minHeight: '400px',
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `
            radial-gradient(circle at 10% 20%, rgba(59, 130, 246, 0.03) 0%, transparent 50%),
            radial-gradient(circle at 90% 80%, rgba(139, 92, 246, 0.03) 0%, transparent 50%),
            radial-gradient(circle at 50% 50%, rgba(0, 255, 136, 0.02) 0%, transparent 50%)
          `,
          zIndex: 0,
        }
      }}
    >
      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        <Typography
          variant={isMobile ? 'h4' : 'h2'}
          component="h2"
          sx={{
            fontWeight: 400,
            mb: { xs: 2, md: 2 },
            color: theme.palette.mode === 'dark' ? '#f1f5f9' : '#1e293b',
            textAlign: 'center',
            fontFamily: '"Charter", "Georgia", serif',
            px: { xs: 2, md: 0 },
            background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}
        >
          Comprehensive Learning Ecosystem
        </Typography>
        <Typography
          variant="body1"
          sx={{
            color: theme.palette.mode === 'dark' ? '#cbd5e1' : '#64748b',
            mb: { xs: 4, md: 8 },
            textAlign: 'center',
            maxWidth: '800px',
            mx: 'auto',
            lineHeight: 1.7,
            fontFamily: '"Kievit", "Roboto", sans-serif',
            px: { xs: 2, md: 0 },
            fontSize: { xs: '0.9rem', md: '1rem' },
          }}
        >
          Knoowl transforms education through AI-powered learning, comprehensive analytics, social multiplayer experiences, blockchain rewards, and mobile accessibility. Every feature is designed to maximize learning outcomes and engagement.
        </Typography>

        {/* Feature Categories */}
        <Box sx={{ mb: { xs: 6, md: 8 } }}>
          {featureCategories.map((category, categoryIndex) => (
            <Box key={categoryIndex} sx={{ mb: { xs: 4, md: 6 } }}>
              {/* Category Header */}
              <Box sx={{ textAlign: 'center', mb: { xs: 3, md: 4 } }}>
                <Typography
                  variant={isMobile ? 'h5' : 'h3'}
                  sx={{
                    fontWeight: 600,
                    color: category.color,
                    mb: 1,
                    fontFamily: '"Charter", "Georgia", serif',
                    textShadow: `0 0 20px ${category.color}40`,
                  }}
                >
                  {category.title}
                </Typography>
                <Box
                  sx={{
                    width: 60,
                    height: 3,
                    background: `linear-gradient(90deg, ${category.color} 0%, ${category.color}80 100%)`,
                    borderRadius: 2,
                    mx: 'auto',
                    boxShadow: `0 0 10px ${category.color}40`,
                  }}
                />
              </Box>

              {/* Feature Cards */}
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: '1fr',
                    sm: 'repeat(2, 1fr)',
                    lg: 'repeat(4, 1fr)',
                  },
                  gap: { xs: 3, md: 4 },
                }}
              >
                {category.features.map((feature, featureIndex) => (
                  <Card
                    key={featureIndex}
                    sx={{
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      transition: 'all 0.3s ease',
                      background: `linear-gradient(135deg, ${feature.color}08 0%, ${feature.color}12 100%)`,
                      border: `1px solid ${feature.color}30`,
                      backdropFilter: 'blur(10px)',
                      position: 'relative',
                      overflow: 'hidden',
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: `linear-gradient(135deg, ${feature.color}05 0%, transparent 50%)`,
                        opacity: 0,
                        transition: 'opacity 0.3s ease',
                      },
                      '&:hover': {
                        transform: 'translateY(-8px)',
                        boxShadow: `0 20px 40px ${feature.color}25`,
                        '&::before': {
                          opacity: 1,
                        },
                      },
                    }}
                  >
                    <CardContent
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        textAlign: 'center',
                        p: { xs: 3, md: 4 },
                        height: '100%',
                        position: 'relative',
                        zIndex: 1,
                      }}
                    >
                      {/* Icon */}
                      <Box
                        sx={{
                          width: { xs: 60, md: 70 },
                          height: { xs: 60, md: 70 },
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          mb: { xs: 2, md: 3 },
                          background: `linear-gradient(135deg, ${feature.color}20 0%, ${feature.color}40 100%)`,
                          color: feature.color,
                          boxShadow: `0 0 20px ${feature.color}30`,
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            boxShadow: `0 0 30px ${feature.color}50`,
                            transform: 'scale(1.1)',
                          },
                        }}
                      >
                        <feature.icon sx={{ fontSize: { xs: 28, md: 35 } }} />
                      </Box>

                      {/* Title */}
                      <Typography
                        variant="h6"
                        component="h3"
                        sx={{
                          fontWeight: 600,
                          mb: { xs: 1.5, md: 2 },
                          color: theme.palette.mode === 'dark' ? '#f1f5f9' : '#1e293b',
                          fontFamily: '"Charter", "Georgia", serif',
                          fontSize: { xs: '1.1rem', md: '1.25rem' },
                        }}
                      >
                        {feature.title}
                      </Typography>

                      {/* Description */}
                      <Typography
                        variant="body2"
                        sx={{
                          color: theme.palette.mode === 'dark' ? '#cbd5e1' : '#64748b',
                          lineHeight: 1.6,
                          flex: 1,
                          fontFamily: '"Kievit", "Roboto", sans-serif',
                          fontSize: { xs: '0.85rem', md: '0.875rem' },
                        }}
                      >
                        {feature.description}
                      </Typography>
                    </CardContent>
                  </Card>
                ))}
              </Box>
            </Box>
          ))}
        </Box>

        {/* Technology Stack */}
        <Box
          sx={{
            mt: { xs: 6, md: 8 },
            p: { xs: 3, md: 6 },
            borderRadius: 3,
            background: `linear-gradient(135deg, ${theme.palette.primary.main}08 0%, ${theme.palette.secondary.main}08 50%, ${theme.palette.neon?.main}08 100%)`,
            border: `1px solid ${theme.palette.primary.main}20`,
            backdropFilter: 'blur(10px)',
            textAlign: 'center',
            mx: { xs: 2, md: 0 },
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: `linear-gradient(135deg, ${theme.palette.primary.main}05 0%, transparent 50%)`,
              opacity: 0,
              transition: 'opacity 0.3s ease',
            },
            '&:hover::before': {
              opacity: 1,
            },
          }}
        >
          <Typography
            variant={isMobile ? 'h5' : 'h3'}
            sx={{
              fontWeight: 400,
              mb: { xs: 2, md: 3 },
              color: theme.palette.mode === 'dark' ? '#f1f5f9' : '#1e293b',
              fontFamily: '"Charter", "Georgia", serif',
              background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            Powered by Advanced Technology
          </Typography>
          
          <Typography
            variant="body1"
            sx={{
              color: theme.palette.mode === 'dark' ? '#cbd5e1' : '#64748b',
              mb: { xs: 3, md: 4 },
              maxWidth: '800px',
              mx: 'auto',
              lineHeight: 1.7,
              fontFamily: '"Kievit", "Roboto", sans-serif',
              fontSize: { xs: '0.9rem', md: '1rem' },
            }}
          >
            Built with cutting-edge technologies including FastAPI, React Native, PostgreSQL with pgvector, OpenAI integration, WebSocket real-time communication, and blockchain smart contracts for a robust, scalable learning platform.
          </Typography>

          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={{ xs: 1, md: 2 }}
            justifyContent="center"
            flexWrap="wrap"
            sx={{ gap: { xs: 1, md: 2 } }}
          >
            <Chip
              label="FastAPI Backend"
              sx={{
                bgcolor: `${theme.palette.primary.main}20`,
                color: theme.palette.primary.main,
                fontWeight: 500,
                fontFamily: '"Kievit", "Roboto", sans-serif',
                fontSize: { xs: '0.75rem', md: '0.875rem' },
                border: `1px solid ${theme.palette.primary.main}30`,
                '&:hover': {
                  bgcolor: `${theme.palette.primary.main}30`,
                  boxShadow: `0 0 10px ${theme.palette.primary.main}30`,
                },
              }}
            />
            <Chip
              label="React Native Mobile"
              sx={{
                bgcolor: `${theme.palette.secondary.main}20`,
                color: theme.palette.secondary.main,
                fontWeight: 500,
                fontFamily: '"Kievit", "Roboto", sans-serif',
                fontSize: { xs: '0.75rem', md: '0.875rem' },
                border: `1px solid ${theme.palette.secondary.main}30`,
                '&:hover': {
                  bgcolor: `${theme.palette.secondary.main}30`,
                  boxShadow: `0 0 10px ${theme.palette.secondary.main}30`,
                },
              }}
            />
            <Chip
              label="PostgreSQL + pgvector"
              sx={{
                bgcolor: `${theme.palette.primary.main}20`,
                color: theme.palette.primary.main,
                fontWeight: 500,
                fontFamily: '"Kievit", "Roboto", sans-serif',
                fontSize: { xs: '0.75rem', md: '0.875rem' },
                border: `1px solid ${theme.palette.primary.main}30`,
                '&:hover': {
                  bgcolor: `${theme.palette.primary.main}30`,
                  boxShadow: `0 0 10px ${theme.palette.primary.main}30`,
                },
              }}
            />
            <Chip
              label="OpenAI Integration"
              sx={{
                bgcolor: `${theme.palette.secondary.main}20`,
                color: theme.palette.secondary.main,
                fontWeight: 500,
                fontFamily: '"Kievit", "Roboto", sans-serif',
                fontSize: { xs: '0.75rem', md: '0.875rem' },
                border: `1px solid ${theme.palette.secondary.main}30`,
                '&:hover': {
                  bgcolor: `${theme.palette.secondary.main}30`,
                  boxShadow: `0 0 10px ${theme.palette.secondary.main}30`,
                },
              }}
            />
            <Chip
              label="Blockchain Rewards"
              sx={{
                bgcolor: `${theme.palette.primary.main}20`,
                color: theme.palette.primary.main,
                fontWeight: 500,
                fontFamily: '"Kievit", "Roboto", sans-serif',
                fontSize: { xs: '0.75rem', md: '0.875rem' },
                border: `1px solid ${theme.palette.primary.main}30`,
                '&:hover': {
                  bgcolor: `${theme.palette.primary.main}30`,
                  boxShadow: `0 0 10px ${theme.palette.primary.main}30`,
                },
              }}
            />
            <Chip
              label="Real-time WebSocket"
              sx={{
                bgcolor: `${theme.palette.secondary.main}20`,
                color: theme.palette.secondary.main,
                fontWeight: 500,
                fontFamily: '"Kievit", "Roboto", sans-serif',
                fontSize: { xs: '0.75rem', md: '0.875rem' },
                border: `1px solid ${theme.palette.secondary.main}30`,
                '&:hover': {
                  bgcolor: `${theme.palette.secondary.main}30`,
                  boxShadow: `0 0 10px ${theme.palette.secondary.main}30`,
                },
              }}
            />
          </Stack>
        </Box>
      </Container>
    </Box>
  );
};

export default Features; 